import {
  CountryCode,
  getCountries,
  getCountryCallingCode,
} from "libphonenumber-js";

export type CountryOption = {
  iso: CountryCode;
  name: string;
  callingCode: string;
};

const regionNames =
  typeof Intl !== "undefined" && "DisplayNames" in Intl
    ? new Intl.DisplayNames(["en"], { type: "region" })
    : null;

const countryName = (iso: CountryCode): string => {
  try {
    return regionNames?.of(iso) ?? iso;
  } catch {
    return iso;
  }
};

export const ALL_COUNTRIES: CountryOption[] = getCountries()
  .map((iso) => ({
    iso,
    name: countryName(iso),
    callingCode: getCountryCallingCode(iso),
  }))
  .sort((a, b) => a.name.localeCompare(b.name));

/**
 * The countries the select should offer.
 *
 * `allowed` is the set we can actually deliver SMS to — the backend keeps it in
 * `countries.sms_enabled` and rejects anything else with
 * `SMS_TWO_FACTOR_REGION_NOT_SUPPORTED`. Offering all 250 meant the form
 * cheerfully accepted a number it already knew would be refused, and the user
 * only found out after submitting.
 *
 * Two deliberate exceptions:
 *
 * - An empty or missing `allowed` falls back to the full list rather than to an
 *   empty dropdown. The list arrives over the network, and a failed request must
 *   degrade to the old behaviour, not to a form nobody can fill in. The backend
 *   is the real gate either way.
 * - `selected` is always kept, even when it is not allowed. Someone whose
 *   enrolled number is in a country we have since switched off still has to see
 *   that country in the select instead of a blank box.
 */
export const filterCountries = (
  allowed: string[] | undefined,
  selected?: CountryCode
): CountryOption[] => {
  if (!allowed?.length) return ALL_COUNTRIES;

  const allowedSet = new Set(allowed);

  return ALL_COUNTRIES.filter(
    ({ iso }) => allowedSet.has(iso) || iso === selected
  );
};

/**
 * Which country the select should start on.
 *
 * The inferred one (timezone, then browser locale) wins when we can deliver
 * there. Otherwise a user in, say, Prague would open the form on Czechia — a
 * country the backend refuses — and have to work out for themselves that the
 * default is the problem.
 */
export const resolveAllowedCountry = (
  allowed: string[] | undefined,
  preferred: CountryCode,
  fallback: CountryCode
): CountryCode => {
  if (!allowed?.length) return preferred;

  const allowedSet = new Set(allowed);

  if (allowedSet.has(preferred)) return preferred;
  if (allowedSet.has(fallback)) return fallback;

  return ALL_COUNTRIES.find(({ iso }) => allowedSet.has(iso))?.iso ?? preferred;
};
